'use client';

import { useEffect, useState } from 'react';
import { Modal } from '@wishket/design-system';
import { usePathname } from 'next/navigation';

import { OnBoardingNewsLetterSetting } from '../NewsLetterSetting';
import { OnBoardingProfile } from '../Profile';

import OnBoardingStart from './OnBoardingStart';

import {
  useDeviceCheck,
  useLocalStorageState,
  useProfile,
} from '@/shared/hooks';
import { ONBOARDING_STATUS, PATH, STORAGE_KEY } from '@/shared/constants';
import { useProfileInfoQuery } from '@/entities/profile';
import { UIOverlayModalProps } from '@/shared/types';

const OnBoardingModal = ({ rootId }: Omit<UIOverlayModalProps, 'isOpen'>) => {
  const pathname = usePathname();
  const { profile } = useProfile();
  const { isDesktop } = useDeviceCheck();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(ONBOARDING_STATUS.START);
  const { data: profileInfo, isLoading: isProfileInfoLoading } =
    useProfileInfoQuery({
      yozmitProfileId: profile?.yozmitProfileId,
    });
  const [tutorialCompleted] = useLocalStorageState(STORAGE_KEY.TUTORIAL, false);

  const handleNextStep = () => {
    setStep(step + 1);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const openModal = () => {
    if (profile?.yozmitOnboardingStatus === undefined) return;

    if (
      profileInfo?.career &&
      profileInfo?.role &&
      profileInfo?.skills &&
      profileInfo?.level
    ) {
      setStep(ONBOARDING_STATUS.END);
    } else {
      setStep(profile.yozmitOnboardingStatus);
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (
      (!profile?.yozmitOnboardingStatus &&
        profile?.yozmitOnboardingStatus !== 0) ||
      profile?.yozmitOnboardingStatus >= ONBOARDING_STATUS.END ||
      isProfileInfoLoading
    ) {
      return;
    }

    if (
      pathname.includes(PATH.DETAIL) &&
      tutorialCompleted &&
      profile?.yozmitOnboardingStatus < ONBOARDING_STATUS.END
    ) {
      openModal();
      return;
    }

    if (!pathname.includes(PATH.DETAIL)) {
      openModal();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, pathname, tutorialCompleted, isProfileInfoLoading]);

  const isPreventModalOpen =
    !profile ||
    profile.yozmitOnboardingStatus === ONBOARDING_STATUS.END ||
    pathname === PATH.SIGNUP_VERIFY;

  if (isPreventModalOpen) return;

  return (
    <Modal
      rootId={rootId}
      isOpen={isOpen}
      variant={isDesktop ? 'default' : 'full'}
      isPreventScroll
    >
      {step === ONBOARDING_STATUS.START && (
        <OnBoardingStart onNextStep={handleNextStep} username={profile.name} />
      )}
      {step === ONBOARDING_STATUS.SET_PROFILE && (
        <OnBoardingProfile onNextStep={handleNextStep} />
      )}
      {step === ONBOARDING_STATUS.END && (
        <OnBoardingNewsLetterSetting onClose={onClose} />
      )}
    </Modal>
  );
};

export default OnBoardingModal;
